import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from '../utils/axios';
import { API_ENDPOINTS } from '../config/api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [churchData, setChurchData] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getFullAvatarUrl = (url) => {
    console.log('🖼️ [AuthContext] Getting full avatar URL for:', url);
    if (!url) {
      console.log('🖼️ [AuthContext] No URL provided, returning empty string');
      return '';
    }
    if (url.startsWith('http')) {
      console.log('🖼️ [AuthContext] URL is already absolute:', url);
      return url;
    }
    // Remove /api/v1 if it exists in the base URL since the upload path already includes it
    const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace('/api/v1', '');
    const fullUrl = `${baseUrl}${url}`;
    console.log('🖼️ [AuthContext] Converted to full URL:', fullUrl);
    return fullUrl;
  };

  const fetchUser = async () => {
    try {
      console.log('👤 [AuthContext] Fetching user data...');
      const res = await axios.get('/auth/me');
      console.log('👤 [AuthContext] Raw user data received:', res.data);
      
      if (res.data) {
        const userData = res.data;
        // Always ensure both avatar and profilePhoto fields are set
        // and that avatarUrl is properly constructed
        
        // First, determine which field has a value (prioritize profilePhoto)
        const photoUrl = userData.profilePhoto || userData.avatar || '';
        console.log('🖼️ [AuthContext] Using photo URL:', photoUrl);
        
        if (photoUrl) {
          // Set both fields for consistency
          userData.profilePhoto = photoUrl;
          userData.avatar = photoUrl;
          
          // Always generate the full URL
          userData.avatarUrl = getFullAvatarUrl(photoUrl);
          console.log('🖼️ [AuthContext] Set avatarUrl to:', userData.avatarUrl);
        }
        
        console.log('👤 [AuthContext] Final user data:', userData);
        setUser(userData);
        return userData;
      }
      return null;
    } catch (error) {
      console.error('❌ [AuthContext] Error fetching user:', error);
      handleLogout();
      return null;
    }
  };

  const fetchChurchData = async (user) => {
    if (user?.church) {
      try {
        const response = await axios.get(API_ENDPOINTS.CHURCHES.BASE + '/me');
        if (response.data.success) {
          setChurchData(response.data.data);
        }
      } catch (err) {
        console.error('Error fetching church data:', err);
      }
    }
  };

  const updateUser = (updates) => {
    console.log('👤 [AuthContext] Updating user with:', updates);
    setUser(currentUser => {
      console.log('👤 [AuthContext] Current user state:', currentUser);
      const newUser = {
        ...currentUser,
        ...updates
      };
      
      // Handle profile photo updates consistently
      // Determine which field to use (prioritize profilePhoto)
      const photoUrl = newUser.profilePhoto || newUser.avatar || '';
      
      if (photoUrl) {
        // Ensure both fields are set for consistency
        newUser.profilePhoto = photoUrl;
        newUser.avatar = photoUrl;
        
        // Always generate the full URL
        console.log('🖼️ [AuthContext] Processing updated photo URL:', photoUrl);
        newUser.avatarUrl = getFullAvatarUrl(photoUrl);
        console.log('🖼️ [AuthContext] Set new avatarUrl to:', newUser.avatarUrl);
      }
      
      console.log('👤 [AuthContext] Final updated user:', newUser);
      return newUser;
    });
  };

  const handleLogin = async (email, password) => {
    setLoading(true);
    try {
      // Make login request
      const res = await axios.post('/auth/login', {
        email,
        password
      });

      if (!res.data.token) {
        throw new Error('Login failed - missing token in response');
      }

      // Save token
      const newToken = res.data.token;
      localStorage.setItem('token', newToken);
      setToken(newToken);
      
      // We no longer need to restore theme preference on login
      // The theme preference should persist regardless of authentication state

      // Always fetch fresh user data to ensure we have complete information
      await fetchUser();
      const userData = await fetchUser();
      if (userData) {
        await fetchChurchData(userData);
      }

      setLoading(false);
      return { 
        success: true, 
        token: newToken,
        user: userData
      };
    } catch (error) {
      setLoading(false);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Login failed'
      };
    }
  };

  const handleRegister = async (userData) => {
    setLoading(true);
    try {
      const res = await axios.post('/auth/register', userData);

      // Save token
      const newToken = res.data.token;
      const newUser = res.data.user;

      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(newUser);
      
      // We no longer need to restore theme preference on login
      // The theme preference should persist regardless of authentication state
      await fetchChurchData(newUser);
      return res.data;
    } catch (error) {
      console.error('Register error:', error.response?.data?.message || error.message);
      throw error;
    }
  };

  const handleLogout = () => {
    // We no longer need to save or reset theme preference on logout
    // The theme preference should persist regardless of authentication state
    
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setChurchData(null);
    setLoading(false);
  };

  const canEdit = () => {
    if (!user) return false;
    return user.role === 'admin' || user.role === 'leader';
  };

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        setToken(storedToken);
        try {
          const userData = await fetchUser();
          if (userData) {
            // Check if we have a saved profile photo in localStorage
            const savedProfilePhoto = localStorage.getItem('userProfilePhoto');
            if (savedProfilePhoto && (!userData.profilePhoto || !userData.avatar)) {
              console.log('🖼️ [AuthContext] Found saved profile photo in localStorage:', savedProfilePhoto);
              // Update the user data with the saved profile photo
              updateUser({
                profilePhoto: savedProfilePhoto,
                avatar: savedProfilePhoto
              });
            }
            
            await fetchChurchData(userData);
            // We no longer need to restore theme preference on auth initialization
            // The theme preference should persist regardless of authentication state
          }
        } catch (error) {
          console.error('Auth initialization failed:', error);
          localStorage.removeItem('token');
          // Don't change theme preference when auth fails
        }
      } else {
        // Don't change theme preference when not authenticated
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const value = {
    user,
    churchData,
    token,
    loading,
    updateUser,
    login: handleLogin,
    logout: handleLogout,
    register: handleRegister,
    canEdit,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
